/**
 * クリエイター一覧の見せ方の切り替えと、自由に動かせる面
 *
 * @description Grid は3列の並び。Explore は札をドラッグとホイールでどこまでも動かせる面。
 *
 *              無限に見せるために、札は格子の升目に置いて**1枚ずつ折り返す**。
 *                sx = wrap(基準X + パンX, 格子の幅) - はみ出し代
 *              札はページ送りと関係なく全員ぶん置く。格子は「画面 + 升目1つ」より
 *              大きくないと折り返しの継ぎ目に穴が空くので、人数が少ないときは
 *              升目が埋まるまで同じ顔ぶれを並べておく（枚数はPHP側で決める）。
 *
 *              慣性スクロールが動いていると面の上でホイールを回したときにページも流れる。
 *              面に入っているあいだは止める。
 */

import { getLenis } from './lib-smooth-scroll';

/**
 * 面の組み方。札はここに配る
 *
 * @description cols x rows は archive-creators.php の升目の最小数と揃える。総数は変えずに
 *              SPだけ縦長の画面に合わせて列を減らし行を増やす。行が足りないと
 *              格子が「画面 + 升目1つ」より低くなって、折り返しの継ぎ目に穴が空く。
 *              札が最小数より多いときは rows をそのぶん伸ばす（列は増やさない）。
 *
 *              cardW は面の幅に対する札の幅、gapX / gapY は札に対するあき、
 *              stagger は隣の列をどれだけ下げるか（升目の高さに対する割合）。
 */
const LAYOUT_PC = { cols: 8, rows: 4, cardW: 268 / 1728, ratio: 451 / 268, gapX: 72 / 268, gapY: 115 / 451, stagger: 0.5 };
const LAYOUT_SP = { cols: 4, rows: 8, cardW: 120 / 393, ratio: 207 / 120, gapX: 32 / 120, gapY: 32 / 207, stagger: 0.5 };

/** SCSSの mq() と同じ境目 */
const SP_MQ = '(max-width: 1024px)';

/** 拡げるほうの上限。いまの見え方がいちばん大きい状態 */
const ZOOM_MAX = 1;

/**
 * 縮めるほうの下限
 *
 * @description 実際はここまで縮まないこともある。札の枚数は決め打ちなので、縮めすぎると
 *              格子が「画面 + 升目1つ」より小さくなって折り返しの継ぎ目に穴が空く。
 *              そうならない最小の倍率を画面ごとに出して、こちらと大きいほうを採る
 */
const ZOOM_MIN = 0.5;

/** ホイール1目盛りあたりの効き。指数に掛けるので、どの倍率でも同じ手応えになる */
const ZOOM_WHEEL = 0.006;

/** ホイールの1回で動かせる上限。マウスは1目盛りが100前後あり、そのままだと飛ぶ */
const ZOOM_WHEEL_CAP = 30;

/** キーの + / - 1回ぶん */
const ZOOM_KEY_STEP = 1.1;

/** 見せ方の覚え書き。記事へ入って戻ったときや再読み込みでも同じ見せ方で開く */
const VIEW_KEY = 'oneview:creators-view';

/** 面を見ているあいだ html に付く印。ヘッダーの出し入れを止めるのに使う */
const EXPLORE_FLAG = 'is-creatorExplore';

/**
 * 覚えている見せ方を読む
 *
 * @returns 覚えていなければ空文字
 */
const readView = (): string => {
    try {
        return sessionStorage.getItem(VIEW_KEY) ?? '';
    } catch {
        // プライベートウィンドウなどで読めないことがある。覚えていない扱いでよい
        return '';
    }
};

/**
 * 見せ方を覚える
 *
 * @param view grid か explore
 * @returns {void}
 */
const writeView = (view: string): void => {
    try {
        sessionStorage.setItem(VIEW_KEY, view);
    } catch {
        // 書けなくても見せ方そのものは動く
    }
};

/** 慣性の追従。小さいほど長く滑る */
const EASE = 0.12;

/** 放したあとの伸び。離した瞬間の速さに掛ける */
const THROW = 12;

/** これ以上動かしたら「掴んで動かした」とみなし、指を離してもリンクへ飛ばさない(px) */
const DRAG_SLOP = 5;

/** 矢印キー1回で動かす量。升目の何ぶんか */
const KEY_STEP = 1;

/** 解除する手続き。非同期遷移で中身ごと作り直すときに使う */
let detach: (() => void) | null = null;

/**
 * 負でも正しく折り返す剰余
 *
 * @param value 値
 * @param size 法
 * @returns 0以上 size 未満
 */
const wrap = (value: number, size: number): number => ((value % size) + size) % size;

/**
 * 一覧の作り直しに備えて後片付けする
 *
 * @returns {void}
 */
const destroyCreatorsView = (): void => {
    detach?.();
    detach = null;
};

/**
 * 一覧の見せ方を組み立てる
 *
 * @returns {void}
 */
const initCreatorsView = (): void => {
    destroyCreatorsView();

    const root = document.querySelector<HTMLElement>('[data-creator-view]');

    if (!root) return;

    const canvas = root.querySelector<HTMLElement>('[data-creator-canvas]');
    const plane = root.querySelector<HTMLElement>('[data-creator-plane]');
    const switchBtns = [...root.querySelectorAll<HTMLButtonElement>('[data-creator-view-btn]')];
    const bar = root.querySelector<HTMLElement>('[data-creator-switch]');

    if (!canvas || !plane) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const tiles = [...plane.querySelectorAll<HTMLElement>('[data-creator-tile]')].map((el) => ({
        el,
        link: el.querySelector<HTMLAnchorElement>('a'),
        bx: 0,
        by: 0,
        // 折り返し後の位置。真ん中の札を選ぶのに使う
        x: 0,
        y: 0,
    }));

    /** いま真ん中にいる札。Tabで止まるのはこの1枚だけ */
    let centre = -1;

    /** 面の大きさと格子 */
    const box = { cw: 1, ch: 1, cellW: 1, cellH: 1, latW: 1, latH: 1 };

    /** パンの位置。目標（w付き）と実際を分けて持つ */
    const pan = { wx: 0, wy: 0, x: 0, y: 0 };

    /** いまの拡大率。ZOOM_MAX がいちばん大きい状態 */
    let zoom = ZOOM_MAX;

    /**
     * いま使う組み方を返す
     *
     * @returns LAYOUT_PC か LAYOUT_SP
     */
    const layout = (): typeof LAYOUT_PC => (window.matchMedia(SP_MQ).matches ? LAYOUT_SP : LAYOUT_PC);

    /**
     * 穴が空かない最小の倍率
     *
     * @description 折り返しは升目1つぶんの余裕で成り立つので、格子は「画面 + 升目1つ」より
     *              大きくないといけない。つまり升目 (cols - 1) 個で画面幅を埋められること。
     *              縦も同じ
     * @returns これ以上は縮められない倍率
     */
    const zoomFloor = (): number => {
        const lay = layout();
        const w = Math.max(1, canvas.clientWidth);
        const h = Math.max(1, canvas.clientHeight);
        const rows = Math.max(lay.rows, Math.ceil(tiles.length / lay.cols));
        // 倍率1のときの升目。倍率はここに掛かるので、必要な倍率は割り算で出る
        const cellW = w * lay.cardW * (1 + lay.gapX);
        const cellH = w * lay.cardW * lay.ratio * (1 + lay.gapY);

        return Math.max(w / (cellW * (lay.cols - 1)), h / (cellH * (rows - 1)));
    };

    /**
     * 倍率を出せる範囲に収める
     *
     * @param value 望みの倍率
     * @returns 収めた倍率
     */
    const clampZoom = (value: number): number => {
        const low = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoomFloor()));

        return Math.min(ZOOM_MAX, Math.max(low, value));
    };

    /**
     * 面の大きさから格子を組み直す
     *
     * @returns {void}
     */
    const measure = (): void => {
        const w = Math.max(1, canvas.clientWidth);
        const lay = layout();
        // 全員ぶん札があるので枚数はクリエイターの人数で変わる。行はその枚数から決める
        const rows = Math.max(lay.rows, Math.ceil(tiles.length / lay.cols));

        // 画面が変わると縮められる限界も変わる。組み直す前に今の倍率を入れ直す
        zoom = clampZoom(zoom);
        box.cw = w * lay.cardW * zoom;
        box.ch = box.cw * lay.ratio;
        box.cellW = box.cw * (1 + lay.gapX);
        box.cellH = box.ch * (1 + lay.gapY);
        box.latW = box.cellW * lay.cols;
        box.latH = box.cellH * rows;

        // 升目の並びは画面幅で変わるので、列と行はここで割り当て直す
        tiles.forEach((tile, i) => {
            const col = i % lay.cols;

            tile.bx = col * box.cellW;
            // 段差は列ごと。折り返しも列の単位なので、継ぎ目でも段差が保たれる
            tile.by = Math.floor(i / lay.cols) * box.cellH + (col % 2 === 1 ? box.cellH * lay.stagger : 0);
            tile.el.style.width = `${box.cw.toFixed(1)}px`;
        });
    };

    /**
     * 面の中心にいちばん近い札を選ぶ
     *
     * @description 札ぜんぶがTabで止まると送るのが大変なうえ、同じ人が何度も出てくる。
     *              止まるのは真ん中の1枚だけにして、矢印キーで面を動かすと選ぶ札も移る。
     * @returns {void}
     */
    const pickCentre = (): void => {
        const cx = box.cw / 2;
        const cy = box.ch / 2;
        const mx = canvas.clientWidth / 2;
        const my = canvas.clientHeight / 2;

        let best = -1;
        let near = Infinity;

        tiles.forEach((tile, i) => {
            const d = Math.hypot(tile.x + cx - mx, tile.y + cy - my);

            if (d < near) {
                near = d;
                best = i;
            }
        });

        if (best === centre || best < 0) return;

        centre = best;

        tiles.forEach((tile, i) => {
            const on = i === centre;

            tile.el.classList.toggle('is-centre', on);
            tile.link?.setAttribute('tabindex', on ? '0' : '-1');
        });
    };

    /**
     * 1フレームぶん置き直す。1枚ずつ折り返す
     *
     * @returns {void}
     */
    const draw = (): void => {
        tiles.forEach((tile) => {
            const x = wrap(tile.bx + pan.x + box.cellW, box.latW) - box.cellW;
            const y = wrap(tile.by + pan.y + box.cellH, box.latH) - box.cellH;

            tile.x = x;
            tile.y = y;
            tile.el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
        });

        pickCentre();
    };


    const drag = { on: false, x0: 0, y0: 0, wx0: 0, wy0: 0, vx: 0, vy: 0, lx: 0, ly: 0, moved: 0, held: -1 };
    let used = false;

    /** 面へ移る直前に読んでいた位置。並びへ戻したときにそこへ返す */
    let gridY = 0;

    /**
     * はじめの合図を引っ込める
     *
     * @returns {void}
     */
    const markUsed = (): void => {
        if (used) return;

        used = true;
        canvas.classList.add('is-used');
    };

    /**
     * 面を拡げる・縮める
     *
     * @description つまんだ点を動かさずに倍率だけ変える。札の位置は「基準 + パン」なので、
     *              その点を中心に全体を相似にするぶんをパンへ足し戻す。目標と実際の
     *              両方に同じ式を当てないと、慣性が残っているあいだに変えたとき点がずれる
     * @param next 望みの倍率
     * @param px 動かしたくない点。面の左からの位置
     * @param py 動かしたくない点。面の上からの位置
     * @returns {void}
     */
    const setZoom = (next: number, px: number, py: number): void => {
        const to = clampZoom(next);
        const f = to / zoom;

        if (f === 1) return;

        zoom = to;
        pan.wx = px * (1 - f) + f * pan.wx;
        pan.wy = py * (1 - f) + f * pan.wy;
        pan.x = px * (1 - f) + f * pan.x;
        pan.y = py * (1 - f) + f * pan.y;
        measure();
        draw();
        markUsed();
    };

    /**
     * 面の中での位置に直す
     *
     * @param x 画面の左からの位置
     * @param y 画面の上からの位置
     * @returns 面の左上を原点にした位置
     */
    const toLocal = (x: number, y: number): [number, number] => {
        const r = canvas.getBoundingClientRect();

        return [x - r.left, y - r.top];
    };

    /** 面に触れている指。2本そろったら、つまんで拡げる操作へ移る */
    const touches = new Map<number, { x: number; y: number }>();

    /** つまんでいる指の間隔。0 ならつまんでいない */
    let pinch = 0;

    /**
     * 掴んで動かす操作を今の位置から始め直す
     *
     * @param x 画面の左からの位置
     * @param y 画面の上からの位置
     * @returns {void}
     */
    const restartDrag = (x: number, y: number): void => {
        drag.on = true;
        drag.x0 = x;
        drag.y0 = y;
        drag.lx = x;
        drag.ly = y;
        drag.wx0 = pan.wx;
        drag.wy0 = pan.wy;
        drag.vx = 0;
        drag.vy = 0;
    };

    const onDown = (e: PointerEvent): void => {
        if (e.pointerType === 'touch') touches.set(e.pointerId, { x: e.clientX, y: e.clientY });

        // 2本目が触れたらつまんで拡げる操作。掴んで動かすほうは降ろす。
        // moved を残しておくと、指を離したときに札のリンクへ飛ばずに済む
        if (touches.size === 2) {
            const [a, b] = [...touches.values()];

            pinch = Math.hypot(a.x - b.x, a.y - b.y);
            drag.on = false;
            drag.moved = DRAG_SLOP + 1;
            canvas.classList.remove('is-grabbing');
            markUsed();
            return;
        }

        drag.moved = 0;
        drag.held = e.pointerId;
        restartDrag(e.clientX, e.clientY);
        canvas.classList.add('is-grabbing');
        markUsed();
    };

    const onMove = (e: PointerEvent): void => {
        if (touches.has(e.pointerId)) touches.set(e.pointerId, { x: e.clientX, y: e.clientY });

        // 指の間隔の変わりぶんをそのまま倍率に写す。動かさない点は2本の真ん中
        if (pinch > 0 && touches.size === 2) {
            const [a, b] = [...touches.values()];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);

            if (dist > 0) {
                const [px, py] = toLocal((a.x + b.x) / 2, (a.y + b.y) / 2);

                setZoom(zoom * (dist / pinch), px, py);
                pinch = dist;
            }

            return;
        }

        if (!drag.on) return;

        pan.wx = drag.wx0 + (e.clientX - drag.x0);
        pan.wy = drag.wy0 + (e.clientY - drag.y0);
        drag.vx = e.clientX - drag.lx;
        drag.vy = e.clientY - drag.ly;
        drag.lx = e.clientX;
        drag.ly = e.clientY;
        drag.moved = Math.max(drag.moved, Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0));

        // 動かし始めてからポインタを面に固定する。札は<a>なので、掴んだままだと
        // ブラウザのリンクドラッグに持っていかれて掴みが外れる。
        // 押しただけの時点では固定しない。固定するとクリックの宛先が面へ移り、
        // 札のリンクが開かなくなる
        if (drag.moved > DRAG_SLOP && !canvas.hasPointerCapture(e.pointerId)) {
            canvas.setPointerCapture(e.pointerId);
        }
    };

    const onUp = (e?: PointerEvent): void => {
        if (e) touches.delete(e.pointerId);

        // 1本だけ残ったら、その指の今の位置から掴んで動かす操作へ戻す。
        // 位置を取り直さないと、つまんでいるあいだに開いた差ぶん一気に飛ぶ
        if (pinch > 0 && touches.size < 2) {
            const rest = [...touches.values()][0];

            pinch = 0;

            if (rest) {
                restartDrag(rest.x, rest.y);
                return;
            }
        }

        if (!drag.on) return;

        drag.on = false;

        if (drag.held >= 0 && canvas.hasPointerCapture(drag.held)) {
            canvas.releasePointerCapture(drag.held);
        }

        drag.held = -1;
        canvas.classList.remove('is-grabbing');
        // 放したあとの伸び。離した瞬間の速さをそのまま目標へ足す
        pan.wx += drag.vx * THROW;
        pan.wy += drag.vy * THROW;
    };

    // ホイールでも動かす。ページが一緒に流れないよう慣性スクロールは面の中では止める
    const onWheel = (e: WheelEvent): void => {
        e.preventDefault();

        // トラックパッドでつまむ操作はCtrl付きのホイールとして届く。ブラウザのズームには渡さず
        // 面の拡大縮小に使う。マウスは1目盛りが100前後あるので、効きに上限をかける
        if (e.ctrlKey || e.metaKey) {
            const step = Math.max(-ZOOM_WHEEL_CAP, Math.min(ZOOM_WHEEL_CAP, e.deltaY));
            const [px, py] = toLocal(e.clientX, e.clientY);

            setZoom(zoom * Math.exp(-step * ZOOM_WHEEL), px, py);
            return;
        }

        pan.wx -= e.deltaX;
        pan.wy -= e.deltaY;
        markUsed();
    };

    const onEnter = (): void => {
        getLenis()?.stop();
    };

    const onLeave = (): void => {
        if (drag.on) return;

        canvas.classList.remove('is-grabbing');
        getLenis()?.start();
    };

    // 動かしたあとの指離しでリンクへ飛ばさない。掴んで動かす操作と押す操作を分ける
    const onClick = (e: MouseEvent): void => {
        if (drag.moved <= DRAG_SLOP) return;

        e.preventDefault();
        e.stopPropagation();
    };

    // 札は<a>。ブラウザ既定のリンクドラッグを始めさせない
    const onDragStart = (e: DragEvent): void => {
        e.preventDefault();
    };

    /** 拡大縮小のキーと倍率。テンキーと「れ」のキーの両方で押せるようにする */
    const ZOOM_KEYS: Record<string, number> = {
        '+': ZOOM_KEY_STEP,
        '=': ZOOM_KEY_STEP,
        '-': 1 / ZOOM_KEY_STEP,
        _: 1 / ZOOM_KEY_STEP,
    };

    /** 矢印キーと動かす向き */
    const KEYS: Record<string, [number, number]> = {
        ArrowLeft: [1, 0],
        ArrowRight: [-1, 0],
        ArrowUp: [0, 1],
        ArrowDown: [0, -1],
    };

    /**
     * 矢印キーで面を動かし、新しく真ん中へ来た札へ進む
     *
     * @param e キー入力
     * @returns {void}
     */
    const onKeyDown = (e: KeyboardEvent): void => {
        if (e.metaKey || e.ctrlKey || e.altKey) return;

        const scale = ZOOM_KEYS[e.key];

        if (scale) {
            e.preventDefault();
            // 手で示した点が無いので、面の真ん中を動かさない点にする
            setZoom(zoom * scale, canvas.clientWidth / 2, canvas.clientHeight / 2);
            return;
        }

        const dir = KEYS[e.key];

        if (!dir) return;

        e.preventDefault();
        markUsed();

        pan.wx += dir[0] * box.cellW * KEY_STEP;
        pan.wy += dir[1] * box.cellH * KEY_STEP;

        // 押した時点の札から進んだ先へフォーカスを移す。動かすたびに測り直すので、
        // 慣性が落ち着くのを待たずに次の真ん中を決める
        const from = centre;

        pan.x = pan.wx;
        pan.y = pan.wy;
        draw();

        if (centre !== from) {
            tiles[centre]?.link?.focus();
        }
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('click', onClick, true);
    canvas.addEventListener('dragstart', onDragStart);
    canvas.addEventListener('pointerenter', onEnter);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    let frame = 0;

    const tick = (): void => {
        const k = reduced ? 1 : EASE;

        pan.x += (pan.wx - pan.x) * k;
        pan.y += (pan.wy - pan.y) * k;
        draw();
        frame = requestAnimationFrame(tick);
    };

    /**
     * 面が見えているあいだだけ動かす
     */
    const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
            if (frame === 0) frame = requestAnimationFrame(tick);
            return;
        }

        cancelAnimationFrame(frame);
        frame = 0;
    }, { threshold: 0 });

    const ro = new ResizeObserver(() => {
        measure();
        draw();
    });

    // ページ送りが画面に入ったら切り替えバーを引っ込める。そこから下は見せ方を
    // 変える場面ではないうえ、出しっぱなしだとフッターのCTAに重なる。
    // isIntersectingで見ると通り過ぎたときに戻ってしまうので、位置で判断する
    const pager = root.querySelector<HTMLElement>('[class$="__pager"]');
    const barTarget = pager ?? root;

    const barIo = bar && pager
        ? new IntersectionObserver(([entry]) => {
            const limit = entry.rootBounds?.height ?? window.innerHeight;

            bar.classList.toggle('is-away', entry.boundingClientRect.top < limit);
        }, { threshold: 0 })
        : null;

    /**
     * 面へ移る前に読んでいた位置へ返す
     *
     * @description 位置は慣性スクロールに書かせる。window.scrollTo で直に書くと慣性側の
     *              目標値が古いままになり、次のホイールで元の位置へ引き戻される
     * @returns {void}
     */
    const restoreGridY = (): void => {
        const lenis = getLenis();

        if (!lenis) {
            window.scrollTo({ top: gridY, left: 0, behavior: 'instant' as ScrollBehavior });
            return;
        }

        lenis.resize();
        lenis.scrollTo(gridY, { immediate: true, force: true });
    };

    /**
     * 見せ方を切り替える
     *
     * @param next 切り替え先。grid か explore
     * @returns {void}
     */
    const setView = (next: string): void => {
        if (root.dataset.creatorView === next) return;

        // 面に移ると並びもページ送りも畳まれてページが短くなり、位置が頭まで詰められる。
        // 戻したときに読んでいた場所へ返せるよう、畳む前に控える
        if (next === 'explore') gridY = window.scrollY;

        root.dataset.creatorView = next;

        switchBtns.forEach((btn) => {
            const on = btn.dataset.creatorViewBtn === next;

            btn.classList.toggle('is-current', on);
            btn.setAttribute('aria-pressed', String(on));
        });

        writeView(next);
        // 面のあいだはヘッダーを引っ込めない。面が画面いっぱいなので、隠れると戻す手が無くなる
        document.documentElement.classList.toggle(EXPLORE_FLAG, next === 'explore');

        if (next !== 'explore') {
            cancelAnimationFrame(frame);
            frame = 0;
            io.unobserve(canvas);
            ro.unobserve(canvas);
            getLenis()?.start();
            restoreGridY();
            // 監視し直すと今の位置で1回呼ばれる。面のあいだページ送りは display:none で
            // 見えなくなり通知が来ないので、戻した位置で判定し直さないと状態が合わない
            barIo?.observe(barTarget);
            return;
        }

        // 面に切り替えたときは、また合図から始める
        used = false;
        canvas.classList.remove('is-used');
        measure();
        draw();
        io.observe(canvas);
        ro.observe(canvas);
        // 面は画面いっぱいなので、ページ送りの位置で引っ込める話は当てはまらない
        barIo?.unobserve(barTarget);
        bar?.classList.remove('is-away');
    };


    switchBtns.forEach((btn) => {
        btn.addEventListener('click', () => setView(btn.dataset.creatorViewBtn ?? 'grid'));
    });

    barIo?.observe(barTarget);

    measure();
    draw();

    // 覚えている見せ方で開き直す
    if (readView() === 'explore') setView('explore');

    detach = () => {
        cancelAnimationFrame(frame);
        frame = 0;
        canvas.removeEventListener('pointerdown', onDown);
        canvas.removeEventListener('click', onClick, true);
        canvas.removeEventListener('dragstart', onDragStart);
        canvas.removeEventListener('pointerenter', onEnter);
        canvas.removeEventListener('pointerleave', onLeave);
        canvas.removeEventListener('wheel', onWheel);
        canvas.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        io.disconnect();
        ro.disconnect();
        barIo?.disconnect();
        document.documentElement.classList.remove(EXPLORE_FLAG);
        getLenis()?.start();
    };
};

export { initCreatorsView, destroyCreatorsView };

document.addEventListener('DOMContentLoaded', initCreatorsView);
